/**
 * Usage counting: what this browser tells the API about a visit, and when it says nothing at all.
 *
 * **Nothing here identifies a person, and nothing is written to the device to make it work**,
 * apart from the owner's own opt-out flag. The view id is random, made per report page view, and
 * lives only in memory; the referrer and campaign are read once at landing and kept in this
 * module. That is what keeps the counting free of a consent banner — see the backend's notes on the
 * usage log, which stores no plate, no address and no identifier.
 *
 * **The owner's own browsers are never counted.** Opening the site once with `?intern=1` sets a flag
 * in this browser; every request from then on carries `X-Check-Internal`, the API skips the write,
 * and the usage events below are not even sent. `?intern=0` clears it. The backend also skips the
 * owner's home networks and anything automated, so this is the part that covers a phone on mobile
 * data. A forged flag only leaves the forger out of the counts, which is harmless.
 */

const INTERNAL_KEY = 'autochecken.intern'

/** The query parameter that switches the opt-out on (`1`) or off (`0`). */
const INTERNAL_PARAMETER = 'intern'

/** What the visitor landed with. Read once, before the router rewrites the address. */
interface Landing {
  referrer?: string
  utmSource?: string
  utmCampaign?: string
}

let landing: Landing = {}

/**
 * Reads the landing facts and applies `?intern=`. Call once, before the app renders.
 *
 * The referrer is kept only when it is another site: once the visitor is inside this one, the
 * browser's referrer is this site's own address, which says nothing. The `intern` parameter is
 * removed from the address bar afterwards so that a copied link does not opt somebody else out.
 */
export function initialiseUsage(): void {
  const url = new URL(window.location.href)

  landing = {
    referrer: externalReferrer(),
    utmSource: url.searchParams.get('utm_source') ?? undefined,
    utmCampaign: url.searchParams.get('utm_campaign') ?? undefined,
  }

  const internal = url.searchParams.get(INTERNAL_PARAMETER)

  if (internal === null) {
    return
  }

  try {
    if (internal === '0') {
      localStorage.removeItem(INTERNAL_KEY)
    } else {
      localStorage.setItem(INTERNAL_KEY, '1')
    }
  } catch {
    // Storage unavailable (a private window, blocked site data): the flag cannot be kept, and the
    // footer will not claim it was.
  }

  url.searchParams.delete(INTERNAL_PARAMETER)
  window.history.replaceState(window.history.state, '', url.toString())
}

/** Whether this browser was opted out of counting with `?intern=1`. */
export function isInternalVisitor(): boolean {
  try {
    return localStorage.getItem(INTERNAL_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * A fresh identifier for one report page view, or `undefined` where the browser cannot make one.
 *
 * It joins the lookup to how the report was read and to a click on its partner link, and to nothing
 * else: it is never stored and the next report gets a new one.
 */
export function newViewId(): string | undefined {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : undefined
}

/**
 * Headers for a report request. Headers rather than query parameters so the URL stays the same for
 * every visitor and the HTTP cache keeps working.
 */
export function usageHeaders(viewId?: string): Record<string, string> {
  const headers: Record<string, string> = {}

  if (isInternalVisitor()) {
    headers['X-Check-Internal'] = '1'
  }
  if (viewId) {
    headers['X-Check-View'] = viewId
  }
  if (landing.referrer) {
    headers['X-Check-Referrer'] = landing.referrer
  }
  if (landing.utmSource) {
    headers['X-Check-Utm-Source'] = landing.utmSource
  }
  if (landing.utmCampaign) {
    headers['X-Check-Utm-Campaign'] = landing.utmCampaign
  }

  return headers
}

/** How a report view was read so far. */
export interface EngagementReport {
  viewId: string
  visibleSeconds: number
  loadMilliseconds?: number
  /** Anchor ids of the sections that came into view, in page order. */
  reachedSections: string[]
}

/** Sends how a report was read. Fire and forget; see `sendEvent`. */
export function sendEngagement(report: EngagementReport): void {
  sendEvent('engagement', report)
}

/**
 * Records a click on a paid partner link.
 *
 * `placement` names which of the link's places on the page was used, so each can be judged on what
 * it earns.
 */
export function sendAffiliateClick(viewId: string | undefined, partner: string, placement: string): void {
  if (viewId) {
    sendEvent('affiliate-click', { viewId, partner, placement })
  }
}

/**
 * Records something the reader did with the report. The API accepts a fixed list of actions and
 * refuses anything else, so a new one is added there first.
 */
export function sendReportAction(viewId: string | undefined, action: 'checklist'): void {
  if (viewId) {
    sendEvent('action', { viewId, action })
  }
}

/**
 * One usage event, sent so that it survives the page going away.
 *
 * `keepalive` is what lets a request started while the page unloads — the reader closing the tab,
 * or a click opening the partner in a new one — still reach the server. `sendBeacon` would do the
 * same but cannot carry a header. Nothing waits for the answer and a failure is swallowed: counting
 * must never cost the reader anything.
 */
function sendEvent(kind: 'engagement' | 'affiliate-click' | 'action', body: unknown): void {
  if (isInternalVisitor()) {
    return
  }

  try {
    fetch(`/api/report-events/${kind}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {})
  } catch {
    // A browser that refuses the request outright; nothing to do.
  }
}

function externalReferrer(): string | undefined {
  if (!document.referrer) {
    return undefined
  }

  try {
    return new URL(document.referrer).host === window.location.host ? undefined : document.referrer
  } catch {
    return undefined
  }
}
